import React from "react";
import { Sparkles, RefreshCw, X } from "lucide-react";

/**
 * "Update available — refresh now?" floating banner.
 *
 * Shows in the bottom-right corner (bottom-centre on mobile) when a new
 * version of the app has been downloaded and is waiting to activate.
 *
 * Two actions:
 *   - "Refresh now" → applies the update + reloads. Drafts persist via
 *     useFormDraft so the user loses nothing.
 *   - "Later" → dismisses the banner for this tab. They'll see it again on
 *     next tab navigation OR they can refresh manually whenever they want.
 *
 * Deliberately non-modal and non-blocking — never interrupts a customer call
 * or a mid-cert form. The user chooses when to refresh.
 */
export const UpdateAvailableBanner = ({ onRefresh, onDismiss }) => {
  return (
    <div
      className="fixed z-[60] bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm"
      role="status"
      aria-live="polite"
      data-testid="update-available-banner"
    >
      <div className="rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 overflow-hidden">
        <div className="px-4 pt-4 pb-3 flex items-start gap-3">
          <div className="shrink-0 mt-0.5 w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              New version of QuoteSorted ready
            </p>
            <p className="text-xs text-slate-500 mt-0.5 leading-snug">
              Your work is auto-saved. Refresh whenever you're ready — your
              drafts will still be here.
            </p>
          </div>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="shrink-0 p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              aria-label="Dismiss update banner"
              data-testid="update-banner-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="px-4 pb-4 pt-1 flex gap-2">
          <button
            onClick={onRefresh}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 active:bg-black text-white text-sm font-medium transition-colors"
            data-testid="update-banner-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh now
          </button>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium transition-colors"
              data-testid="update-banner-later"
            >
              Later
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateAvailableBanner;
